import { ApolloError } from '@apollo/client';
import { ReturnHotKeyIcon } from '@modules/common/components/HotKeyIcons';
import { useMuiModalContext } from '@modules/common/components/Modal/ModalContext';
import { useNotificationContext } from '@modules/common/components/Notification/NotificationContext';
import {
  HELP_CENTER_HOW_TO_CONFIGURE_SSO_USING_SAML,
  SSO_CONFIGURED_TIMEOUT,
} from '@modules/workspaceSettings/constants';
import {
  FETCH_TENANT_SSO_SETTINGS,
  useFetchTenantSSOSettings,
} from '@modules/workspaceSettings/hooks/useFetchTenantSSOSettings';
import { HelpRounded } from '@mui/icons-material';
import { LoadingButton } from '@mui/lab';
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  DialogActions,
  DialogContent,
  Link,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useUpdateTenantSsoMutation } from 'generated/graphql';
import { SyntheticEvent, useState } from 'react';
import { useGetUser } from 'v2/core/services/user';

interface ConfigureSAMLSSOConfigModalContentProps {
  onBack: () => void;
}

export const ConfigureSAMLSSOConfigModalContent = ({
  onBack,
}: ConfigureSAMLSSOConfigModalContentProps) => {
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const { closeModal } = useMuiModalContext();
  const { showSuccessNotification } = useNotificationContext();
  const { ssoSettings, isLoading: isFetchingSSOSettings } =
    useFetchTenantSSOSettings();
  const isSSOEnabled = ssoSettings?.isEnabled;

  const [metadataURL, setMetadataURL] = useState<string>('');
  const {
    tenant: { id: tenantID },
  } = useGetUser();

  const [updateTenantSSO, { loading: isUpdatingSSO }] =
    useUpdateTenantSsoMutation({
      variables: {
        request: {
          tenantID,
          saml: {
            metadataURL,
          },
        },
      },
      refetchQueries: [FETCH_TENANT_SSO_SETTINGS],
    });

  const onSubmit = async (event: SyntheticEvent) => {
    event.preventDefault();
    try {
      await updateTenantSSO();
      closeModal();
      showSuccessNotification({
        contentComponent: isSSOEnabled
          ? 'SSO Modified'
          : 'You can test it out by logging out and signing in through SSO.',
        props: {
          isClosable: true,
          title: isSSOEnabled ? '' : 'SSO Configured',
          timeout: SSO_CONFIGURED_TIMEOUT,
        },
      });
    } catch (error) {
      if (error instanceof ApolloError) {
        return setErrorMessage(error.message);
      }
      setErrorMessage('Some error occurred. Please try again.');
    }
    return null;
  };
  const isLoading = isFetchingSSOSettings || isUpdatingSSO;
  const isSubmitDisabled = metadataURL.trim() === '' || isLoading;

  return (
    <Box component="form" onSubmit={onSubmit}>
      <DialogContent dividers>
        {!!errorMessage && (
          <Alert severity="error" sx={{ mb: 2 }}>
            <AlertTitle>Error Configuring SSO</AlertTitle>
            {errorMessage}
          </Alert>
        )}
        <Typography variant="body2" color="text.secondary" mb={2}>
          Please add the Metadata URL to configure the SSO{' '}
        </Typography>
        <TextField
          required
          value={metadataURL}
          fullWidth
          size="small"
          label="Metadata URL"
          autoComplete="off"
          placeholder="Metadata URL"
          onChange={(event) => {
            setErrorMessage(null);
            setMetadataURL(event.target.value);
          }}
        />
        <Stack direction="row" alignItems="center" spacing={1} mt={2.5}>
          <HelpRounded fontSize="small" color="disabled" />
          <Typography variant="caption">
            Need help in configuring SSO using SAML?
          </Typography>
          <Link
            href={HELP_CENTER_HOW_TO_CONFIGURE_SSO_USING_SAML}
            target="_blank"
            display="flex"
            alignItems="center"
          >
            <Typography variant="caption">See Documentation</Typography>
          </Link>
        </Stack>
      </DialogContent>
      <DialogActions sx={{ m: 1, mr: 2 }}>
        <Button variant="outlined" onClick={onBack} disabled={isLoading}>
          Back
        </Button>
        <LoadingButton
          variant="contained"
          type="submit"
          disabled={isSubmitDisabled}
          loading={isLoading}
          endIcon={<ReturnHotKeyIcon isDisabled={isSubmitDisabled} />}
        >
          Submit
        </LoadingButton>
      </DialogActions>
    </Box>
  );
};
